import { useState } from 'react';
import { Alert, api, Badge, Button, fmtDate, Input, ListPage, Modal, Select, useAuth, useFetch, useForm, usePagedList, UserRole } from '@greenrinse/ui';
import type { Country, Partner } from './types';

export interface UserRow {
  id: string; fullName: string; email: string | null; phone: string | null; role: UserRole; partnerId: string | null; partnerName: string | null; countryId: string | null;
  countryCode: string | null; preferredLanguage: string; isActive: boolean; hasPin: boolean; lastLoginAtUtc: string | null; createdAtUtc: string;
}

const OPERATOR_ROLES: UserRole[] = ['SuperAdmin', 'RegionalManager', 'OperationsSupervisor', 'Finance', 'TeamLead', 'Washer'];
const PARTNER_ROLES: UserRole[] = ['PartnerAdmin', 'PartnerViewer'];
const LANGS = [{ value: 'en', label: 'English' }, { value: 'ar', label: 'العربية' }, { value: 'hi', label: 'हिन्दी' }, { value: 'ur', label: 'اردو' }, { value: 'bn', label: 'বাংলা' }, { value: 'tl', label: 'Tagalog' }];

export function UserForm({ user, partnerId, onSaved, onCancel }: { user: UserRow | null; partnerId?: string; onSaved: () => void; onCancel: () => void }) {
  const me = useAuth().user;
  const partners = useFetch<{ items: Partner[] }>(me?.role === 'SuperAdmin' || me?.role === 'RegionalManager' ? '/api/partners?pageSize=200' : null);
  const countries = useFetch<Country[]>('/api/countries');
  const roles = partnerId ? PARTNER_ROLES : me?.role === 'PartnerAdmin' ? PARTNER_ROLES : [...OPERATOR_ROLES, ...PARTNER_ROLES];
  const form = useForm({
    fullName: user?.fullName ?? '', email: user?.email ?? '', phone: user?.phone ?? '', role: user?.role ?? (partnerId ? 'PartnerViewer' : 'Washer'),
    partnerId: user?.partnerId ?? partnerId ?? '', countryId: user?.countryId ?? '', preferredLanguage: user?.preferredLanguage ?? 'en', isActive: user?.isActive ?? true, password: '',
  });
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const isPartnerRole = PARTNER_ROLES.includes(form.values.role as UserRole);

  const save = () => form.submit(async (v) => {
    const body = { ...v, email: v.email || null, phone: v.phone || null, partnerId: isPartnerRole ? (v.partnerId || partnerId || null) : null, countryId: v.countryId || null, password: v.password || null };
    if (user) { await api(`/api/users/${user.id}`, { method: 'PUT', body }); onSaved(); }
    else {
      const res = await api<{ user: UserRow; temporaryPassword: string | null }>('/api/users', { method: 'POST', body });
      if (res.temporaryPassword) setTempPassword(res.temporaryPassword); else onSaved();
    }
  });

  if (tempPassword) {
    return (
      <div className="gr-stack">
        <Alert tone="success">User created. Share this temporary password once — it is not shown again:</Alert>
        <div className="gr-card gr-card--tonal gr-card--sm" style={{ fontFamily: 'monospace', fontSize: 20, textAlign: 'center' }}>{tempPassword}</div>
        <Button onClick={onSaved}>Done</Button>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="gr-form-grid">
      {form.message && <div className="gr-span-2" style={{ marginBottom: 12 }}><Alert tone="error">{form.message}</Alert></div>}
      <Input label="Full name" {...form.bind('fullName')} />
      <Select label="Role" {...form.bind('role')} options={roles.map((r) => ({ value: r, label: r }))} />
      <Input label="Email" type="email" {...form.bind('email')} />
      <Input label="Phone (E.164)" placeholder="+971501234567" {...form.bind('phone')} />
      {isPartnerRole && !partnerId && <Select label="Partner" {...form.bind('partnerId')} options={[{ value: '', label: '— select —' }, ...(partners.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }))]} />}
      {!isPartnerRole && <Select label="Country (required for Regional Manager)" {...form.bind('countryId')} options={[{ value: '', label: '— all —' }, ...(countries.data ?? []).map((c) => ({ value: c.id, label: c.name }))]} />}
      <Select label="Language" {...form.bind('preferredLanguage')} options={LANGS} />
      <Input label={user ? 'New password (leave blank to keep)' : 'Password (blank = generate)'} type="password" autoComplete="new-password" {...form.bind('password')} />
      <label className="gr-row" style={{ marginBottom: 16 }}><input type="checkbox" checked={form.values.isActive} onChange={(e) => form.set('isActive', e.target.checked)} /> Active</label>
      <div className="gr-span-2 gr-row" style={{ justifyContent: 'flex-end' }}>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={form.busy}>Save</Button>
      </div>
    </form>
  );
}

export default function UsersPage({ partnerId }: { partnerId?: string }) {
  const [role, setRole] = useState('');
  const list = usePagedList<UserRow>('/api/users', { partnerId, role: role || undefined, includeInactive: true });
  const [editing, setEditing] = useState<UserRow | 'new' | null>(null);

  return (
    <>
      <ListPage<UserRow>
        title="Users" state={list} rowKey={(u) => u.id} onRowClick={(u) => setEditing(u)}
        actions={<Button onClick={() => setEditing('new')}>Add user</Button>}
        filters={!partnerId && (
          <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">All roles</option>
            {[...OPERATOR_ROLES, ...PARTNER_ROLES].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        )}
        columns={[
          { key: 'name', header: 'User', render: (u) => <><strong>{u.fullName}</strong><div className="gr-muted" style={{ fontSize: 13 }}>{[u.email, u.phone].filter(Boolean).join(' · ')}</div></> },
          { key: 'role', header: 'Role', render: (u) => <Badge tone={u.role.startsWith('Partner') ? 'accent' : 'default'}>{u.role}</Badge> },
          { key: 'scope', header: 'Scope', render: (u) => u.partnerName ?? u.countryCode ?? 'All' },
          { key: 'lang', header: 'Lang', render: (u) => u.preferredLanguage, width: 60 },
          { key: 'login', header: 'Last login', render: (u) => fmtDate(u.lastLoginAtUtc) },
          { key: 'status', header: '', render: (u) => u.isActive ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge> },
        ]}
      />
      <Modal open={editing !== null} title={editing === 'new' ? 'New user' : 'Edit user'} onClose={() => setEditing(null)}>
        {editing !== null && <UserForm user={editing === 'new' ? null : editing} partnerId={partnerId} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
      </Modal>
    </>
  );
}
